<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        app-name="OnePlaceHQ"
        product-name="Gym"
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="dashboard-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('dashboard.title') }}</h1>
          <p class="page-subtitle">Welcome back!</p>
        </div>
        <IntercomButton variant="primary" @click="$router.push('/members/new')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('members.newMember') }}</span>
        </IntercomButton>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <!-- Stats Grid -->
      <div v-else class="stats-grid">
        <IntercomCard class="stat-card">
          <div class="stat-label">{{ $t('dashboard.totalMembers') }}</div>
          <div class="stat-value">{{ stats.total_members || 0 }}</div>
        </IntercomCard>

        <IntercomCard class="stat-card success">
          <div class="stat-label">{{ $t('dashboard.activeMembers') }}</div>
          <div class="stat-value">{{ stats.active_members || 0 }}</div>
        </IntercomCard>

        <IntercomCard class="stat-card warning">
          <div class="stat-label">{{ $t('dashboard.expiringThisWeek') }}</div>
          <div class="stat-value">{{ stats.expiring_this_week || 0 }}</div>
        </IntercomCard>

        <IntercomCard class="stat-card info">
          <div class="stat-label">{{ $t('dashboard.todayCollections') }}</div>
          <div class="stat-value">{{ formatCurrency(stats.collections_today || 0) }}</div>
        </IntercomCard>
      </div>

      <!-- Two Column Layout -->
      <div class="two-column-layout">
        <!-- Expiring Members -->
        <IntercomCard class="section-card">
          <div class="section-header">
            <h3>{{ $t('dashboard.expiringMembers') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/members?status=expiring')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>

          <div v-if="expiringMembers.length === 0" class="empty-state">
            <p>{{ $t('dashboard.noExpiringMembers') }}</p>
          </div>

          <div v-else class="member-list">
            <div
              v-for="member in expiringMembers"
              :key="member.id"
              class="member-item"
              @click="$router.push(`/members/${member.id}`)"
            >
              <div class="member-info">
                <div class="member-name">{{ member.name }}</div>
                <div class="member-phone">{{ member.phone }}</div>
              </div>
              <div class="member-expiry">
                <span class="expiry-badge" :class="getExpiryClass(member.membership_end)">
                  {{ formatExpiryDays(member.membership_end) }}
                </span>
              </div>
            </div>
          </div>
        </IntercomCard>

        <!-- Recent Payments -->
        <IntercomCard class="section-card">
          <div class="section-header">
            <h3>{{ $t('dashboard.recentPayments') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/payments')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>

          <div v-if="recentPayments.length === 0" class="empty-state">
            <p>{{ $t('dashboard.noRecentPayments') }}</p>
          </div>

          <div v-else class="payment-list">
            <div
              v-for="payment in recentPayments"
              :key="payment.id"
              class="payment-item"
            >
              <div class="payment-info">
                <div class="payment-member">{{ payment.member_name }}</div>
                <div class="payment-receipt">{{ payment.receipt_number }}</div>
              </div>
              <div class="payment-amount">
                {{ formatCurrency(payment.amount) }}
              </div>
            </div>
          </div>
        </IntercomCard>
      </div>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomSpinner } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'
import dayjs from 'dayjs'

const router = useRouter()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { getDashboard, globalSearch } = useGym()

// Navigation sections for sidebar (translated)
const { navSections } = useNavigation()

// State
const loading = ref(true)
const stats = ref({})
const expiringMembers = ref([])
const recentPayments = ref([])

// Computed
const businessName = computed(() => user.value?.business?.name || '')

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatExpiryDays = (date) => {
  if (!date) return ''
  const days = dayjs(date).diff(dayjs(), 'day')
  if (days < 0) return `${Math.abs(days)} ${t('members.daysAgo')}`
  if (days === 0) return t('members.today')
  return `${days} ${t('members.daysRemaining')}`
}

const getExpiryClass = (date) => {
  if (!date) return ''
  const days = dayjs(date).diff(dayjs(), 'day')
  if (days < 0) return 'expired'
  if (days <= 3) return 'critical'
  if (days <= 7) return 'warning'
  return 'normal'
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const fetchDashboard = async () => {
  try {
    loading.value = true
    const response = await getDashboard()
    stats.value = response
    expiringMembers.value = response.expiring_members || []
    recentPayments.value = response.recent_payments || []
  } catch (error) {
    console.error('Failed to fetch dashboard:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDashboard()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: 0;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.stat-card {
  padding: var(--space-5);
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-2);
}

.stat-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.stat-card.success .stat-value {
  color: var(--color-success);
}

.stat-card.warning .stat-value {
  color: var(--color-warning);
}

.stat-card.info .stat-value {
  color: var(--color-blue);
}

.two-column-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--space-6);
}

.section-card {
  padding: var(--space-5);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.section-header h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.empty-state {
  text-align: center;
  padding: var(--space-8);
  color: var(--color-gray-400);
}

.member-list,
.payment-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.member-item,
.payment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background-color 0.2s;
}

.member-item:hover {
  background: var(--color-gray-100);
}

.member-name,
.payment-member {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.member-phone,
.payment-receipt {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.expiry-badge {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
}

.expiry-badge.expired {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.expiry-badge.critical {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.expiry-badge.warning {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

.expiry-badge.normal {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.payment-amount {
  font-weight: var(--font-weight-semibold);
  color: var(--color-success);
}

@media (max-width: 768px) {

  .page-header {
    flex-direction: column;
    gap: var(--space-4);
  }

  .two-column-layout {
    grid-template-columns: 1fr;
  }
}
</style>
